'use client';

import TabContent from '@/components/tabs/TabContent';
import TabViewContainer from '@/components/tabs/TabViewContainer';
import routes from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { JSX } from 'react';
import React from 'react';
import { MdAdd, MdArrowRight, MdCreditCard, MdOutlinePayments } from 'react-icons/md';
import RequestTable from '../components/tables/RequestTable';
import StatementTable from '../components/tables/StatementTable';
import AddPaymentModal from '../components/AddPaymentModal';
import styles from './billing-screen.module.css';
import { useQuery } from '@tanstack/react-query';
import { BillingApi } from '../billing.api';
import { BillingRequestDto, PaymentCardDto, REQUEST_TYPE_NAMES } from '../billing.types';
import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';
import { getFrontendConfig } from '@/modules/config/engine/frontend-config';
import moment from 'moment';

const formatRequestId = (id: number) => {
	return `XB-${id.toString().padStart(10, '0')}`;
};

const formatDate = (dateString?: string) => {
	if (!dateString) return 'N/A';
	return moment(dateString).format('MM/DD/YYYY');
};

const BillingScreen = () => {
	const navigate = useNavigate();
	const hasRoles = useAuthenticationStore((state) => state.hasRoles);
	const [paymentModalVisible, setPaymentModalVisible] = React.useState(false);

	const frontendConfigQuery = useQuery({
		queryKey: ['frontend-config'],
		queryFn: async () => (await getFrontendConfig()).data,
	});
	const canManageCards =
		hasRoles([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]) &&
		frontendConfigQuery.data?.EnableCardManagement === true;

	// Fetch billing info requests
	const billingInfoRequestsQuery = useQuery({
		queryKey: ['billingInfoRequests'],
		queryFn: BillingApi.getBillingInfoRequests,
	});

	// Fetch payment method requests
	const paymentMethodRequestsQuery = useQuery({
		queryKey: ['paymentMethodRequests'],
		queryFn: BillingApi.getPaymentMethodRequests,
	});

	// Fetch payment cards
	const paymentCardsQuery = useQuery({
		queryKey: ['paymentCards'],
		queryFn: BillingApi.getPaymentCards,
	});

	// Combine and transform requests for the table
	const requestData = React.useMemo(() => {
		const billingRequests: BillingRequestDto[] = (billingInfoRequestsQuery.data || []).map(r => ({
			...r,
			RequestType: 'BillingInfo' as const
		}));

		const paymentRequests: BillingRequestDto[] = (paymentMethodRequestsQuery.data || []).map(r => ({
			...r,
			RequestType: 'PaymentMethod' as const
		}));

		return [...billingRequests, ...paymentRequests]
			.sort((a, b) => {
				const dateA = a.DateRequested ? new Date(a.DateRequested).getTime() : 0;
				const dateB = b.DateRequested ? new Date(b.DateRequested).getTime() : 0;
				return dateB - dateA; // Sort descending (newest first)
			});
	}, [billingInfoRequestsQuery.data, paymentMethodRequestsQuery.data]);

	// Transform data for display in the table
	const displayData = requestData.map(request => ({
		...request,
		requestId: formatRequestId(request.Id),
		date: formatDate(request.DateRequested),
		type: REQUEST_TYPE_NAMES[request.RequestType],
		status: request.Status || 'Unknown'
	}));

	const isLoading = billingInfoRequestsQuery.isLoading || paymentMethodRequestsQuery.isLoading;

	const refetchRequests = () => {
		billingInfoRequestsQuery.refetch();
		paymentMethodRequestsQuery.refetch();
		paymentCardsQuery.refetch();
	};

	// Get the default payment card or the first card
	const defaultPaymentCard = React.useMemo(() => {
		const cards = paymentCardsQuery.data || [];
		const defaultCard = cards.find(card => card.IsDefault);
		return defaultCard || cards[0];
	}, [paymentCardsQuery.data]);

	// Format payment card display text
	const formatPaymentMethod = (card?: PaymentCardDto): string => {
		if (!card) return 'No payment method';

		const cardType = card.CardType || 'Card';
		// Capitalize first letter of card type
		const formattedCardType = cardType.charAt(0).toUpperCase() + cardType.slice(1).toLowerCase();
		const last4 = card.Last4 || 'XXXX';
		return `${formattedCardType} ****${last4}`;
	};

	const columns: {
		field: 'period' | 'balance' | 'action';
		header: string;
	}[] = [
			{ field: 'period', header: 'Pay Period' },
			{ field: 'balance', header: 'Balance' },
			{ field: 'action', header: 'Action' },
		];

	const requestColumns: {
		field: 'requestId' | 'date' | 'type' | 'status';
		header: string;
		sortable?: boolean;
	}[] = [
			{ field: 'requestId', header: 'Request ID', sortable: true },
			{ field: 'date', header: 'Date', sortable: true },
			{ field: 'type', header: 'Request Type', sortable: true },
			{ field: 'status', header: 'Status', sortable: true },
		];

	const tabs = [
		{
			tabName: 'Statements',
			tabContent: (
				<TabContent>
					<div className="mt-2 mb-[18px]">
						<StatementTable columns={columns} />
					</div>
				</TabContent>
			),
		},
		{
			tabName: 'Requests',
			tabContent: (
				<TabContent>
					<div className="my-2">
						<RequestTable
							data={displayData}
							columns={requestColumns}
							loading={isLoading}
							onActionComplete={refetchRequests}
						/>
					</div>
				</TabContent>
			),
		},
	];

	const BillingCard = (route: string, icon: JSX.Element, btnName: string, btnText: string) => {
		return (
			<button
				type="button"
				className={styles.paymentContainerButton}
				onClick={() => navigate(route)}
			>
				<div className="flex items-center gap-4">
					{icon}
					<div className={styles.paymentContainerButtonInfo}>
						<p className={styles.paymentContainerButtonName}>{btnName}</p>
						<p className={styles.paymentContainerButtonSubtext}>{btnText}</p>
					</div>
				</div>
				<MdArrowRight size={24} className="text-onSurfaceVariant" />
			</button>
		);
	}

	return (
		<div className="flex h-full">
			<div className="w-full">
				<p className={styles.pageTitle}>Billing</p>
				<div className="flex items-center justify-between gap-4">
					<h2 className={styles.subHeader}>Payment Accounts</h2>
					{canManageCards && (
						<RoundedFilledButton
							id="billing-add-card"
							label="Add Payment Method"
							icon={MdAdd}
							onClick={() => setPaymentModalVisible(true)}
						/>
					)}
				</div>

				<div className={styles.paymentContainer}>
					{BillingCard(
						routes['/billing/payment-method'],
						(<MdCreditCard size={24} color="black" />),
						'Payment Method',
						paymentCardsQuery.isLoading ? 'Loading...' : formatPaymentMethod(defaultPaymentCard),
					)}
					<div className={styles.paymentContainerButtonDivider} />
					{BillingCard(
						routes['/billing/billing-type'],
						(<MdOutlinePayments size={24} color="black" />),
						'Billing Type', 'Prepaid',
					)}
				</div>

				<div className="flex flex-1 flex-col w-full">
					<TabViewContainer tabList={tabs} />
				</div>
			</div>

			{canManageCards && (
				<AddPaymentModal
					visible={paymentModalVisible}
					setVisible={setPaymentModalVisible}
					onCardAdded={() => paymentCardsQuery.refetch()}
				/>
			)}
		</div>
	);
};

export default BillingScreen;
